/**
 * B2 (revenue build list, Kabir 2026-10-11 ruling 4): the price band of a recorded sale or deposit.
 *
 * AN ANALYTIC BUCKET ONLY. The band exists so the owner's funnel and revenue reports can be split by the
 * size of the piece. It is NEVER shown to a customer, NEVER an input to any reply, qualification, routing,
 * VIP or pricing logic, and no file in packages/agents may import it (priceBand.test.ts greps for that).
 * CLAUDE.md section 3: nothing may be differentiated by inferred wealth, and a band that fed a decision
 * would be exactly that.
 *
 * It is derived in code, from integer cents (F-16, the one money module reads the cents), at the moment an
 * outcome is written, and stored as a code. Nothing here reads a model's output.
 *
 * EDGE SEMANTICS. Each edge is the LOWER bound of the band above it and is INCLUSIVE:
 *   under_1k   1 to 99,999 cents          ($0.01 to $999.99)
 *   1k_5k      100,000 to 499,999         ($1,000.00 to $4,999.99)
 *   5k_15k     500,000 to 1,499,999       ($5,000.00 to $14,999.99)
 *   15k_50k    1,500,000 to 4,999,999     ($15,000.00 to $49,999.99)
 *   50k_plus   5,000,000 and up           ($50,000.00 and up)
 * So $1,000.00 exactly is `1k_5k`. Zero, negative, fractional and unsafe amounts have no band: they throw,
 * because a band for a figure that is not a sale amount would be an invented number.
 *
 * AWAITING RATIFICATION (Kabir): the edges below are the build list's recommendation (under $1k, $1k to $5k,
 * $5k to $15k, $15k to $50k, $50k and over). Changing them re-buckets nothing already stored: rows keep the
 * code they were written with, so a change needs a deliberate re-band migration.
 */
export const PRICE_BAND_EDGES_CENTS = [100_000, 500_000, 1_500_000, 5_000_000] as const;

export const PRICE_BAND_CODES = ["under_1k", "1k_5k", "5k_15k", "15k_50k", "50k_plus"] as const;
export type PriceBand = (typeof PRICE_BAND_CODES)[number];

export const isPriceBand = (word: string): word is PriceBand => (PRICE_BAND_CODES as readonly string[]).includes(word);

export function priceBandOf(amountCents: number): PriceBand {
  if (!Number.isSafeInteger(amountCents) || amountCents <= 0) {
    throw new Error("price band: the amount must be a positive whole number of cents; a figure that is not a sale amount has no band.");
  }
  let i = 0;
  while (i < PRICE_BAND_EDGES_CENTS.length && amountCents >= PRICE_BAND_EDGES_CENTS[i]!) i += 1;
  return PRICE_BAND_CODES[i]!;
}
